// Admin Dashboard - Prospect Management with Notes, Archive, and Provisioning functionality
// UPDATED: Jan 7, 2026 - Added user provisioning for FleetVision access
// UPDATED: Jan 2026 - Added organization users management
import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../../context/AdminContext'
import { useAuth } from '../../context/AuthContext'
import { n8nApi } from '../../services/n8nApi'
import { STAGE_INFO } from '../../config/stages'
import OrganizationUsersModal from './components/OrganizationUsersModal'

const StageBadge = ({ stage }) => {
  const info = STAGE_INFO[stage]
  
  const getStageColor = (stage) => {
    if (!stage) return 'bg-slate-100 text-slate-700'
    if (stage.startsWith('01') || stage.startsWith('02')) return 'bg-blue-100 text-blue-700'
    if (stage === '03_GENERIC_DEMO') return 'bg-purple-100 text-purple-700'
    if (stage.startsWith('03B') || stage.startsWith('04') || stage.startsWith('05')) return 'bg-amber-100 text-amber-700'
    if (stage.startsWith('06') || stage.startsWith('07')) return 'bg-cyan-100 text-cyan-700'
    if (stage.startsWith('10')) return 'bg-green-100 text-green-700'
    if (stage === '11_PROVISIONED') return 'bg-emerald-100 text-emerald-800'
    if (stage.startsWith('99')) return 'bg-red-100 text-red-700'
    return 'bg-slate-100 text-slate-700'
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(stage)}`}>
      {info?.name || stage || 'Unknown'}
    </span>
  )
}

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return (
      <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  
  return sortConfig.direction === 'asc' ? (
    <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

// Confirmation Modal for Personalised Demo Initiation
const ConfirmModal = ({ isOpen, onClose, onConfirm, prospect, isLoading }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <h3 className="text-lg font-semibold text-brand-950 mb-2">
            Start Personalised Demo?
          </h3>
          <p className="text-slate-600 mb-4">
            This will move <strong>{prospect?.full_name}</strong> from{' '}
            <strong>{prospect?.companyName || prospect?.company_name}</strong> to the consent flow.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              <strong>Important:</strong> Only proceed after discussing with the prospect on their intro call.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing...
                </>
              ) : (
                'Yes, Start Personalised Demo'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Notes Modal for viewing/editing prospect notes
const NotesModal = ({ isOpen, onClose, onSave, prospect, isLoading }) => {
  const [notes, setNotes] = useState('')
  
  useEffect(() => {
    if (prospect) {
      setNotes(prospect.notes || '')
    }
  }, [prospect])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 animate-fade-in">
          <h3 className="text-lg font-semibold text-brand-950 mb-1">
            Notes for {prospect?.full_name}
          </h3>
          <p className="text-sm text-slate-500 mb-4">{prospect?.company_name}</p>
          
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this prospect..."
            rows={6}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />

          <div className="flex gap-3 mt-4">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(prospect.firebase_uid, notes)}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Notes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Archive Confirmation Modal
const ArchiveModal = ({ isOpen, onClose, onConfirm, prospect, isArchiving, isLoading }) => {
  if (!isOpen) return null

  const action = isArchiving ? 'Archive' : 'Unarchive'

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <h3 className="text-lg font-semibold text-brand-950 mb-2">
            {action} Prospect?
          </h3>
          <p className="text-slate-600 mb-4">
            {isArchiving ? (
              <>
                This will archive <strong>{prospect?.full_name}</strong> from{' '}
                <strong>{prospect?.company_name}</strong>. They will see an "account inactive" message when they log in.
              </>
            ) : (
              <>
                This will restore <strong>{prospect?.full_name}</strong> from{' '}
                <strong>{prospect?.company_name}</strong>. They will be able to continue their onboarding.
              </>
            )}
          </p>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 ${
                isArchiving 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing...
                </>
              ) : (
                `Yes, ${action}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Provision User Confirmation Modal
const ProvisionModal = ({ isOpen, onClose, onConfirm, prospect, isLoading, error }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-brand-950">Provision FleetVision User?</h3>
          </div>
          
          <p className="text-slate-600 mb-4">
            This will create a FleetVision account for <strong>{prospect?.full_name}</strong> from{' '}
            <strong>{prospect?.company_name}</strong>.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-slate-700 mb-2">This action will:</p>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Create their FleetVision user account
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Send welcome email with setup instructions
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Grant access to {prospect?.company_name}'s fleet data
              </li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 mb-4">
            Email will be sent to: <strong>{prospect?.email}</strong>
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Provisioning...
                </>
              ) : (
                'Yes, Provision User'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Resend DocuSign Envelope Confirmation Modal
const ResendEnvelopeModal = ({ isOpen, onClose, onConfirm, prospect, isLoading, error }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-brand-950">Resend DocuSign Envelope?</h3>
          </div>

          <p className="text-slate-600 mb-4">
            A new Data Access Agreement will be emailed to <strong>{prospect?.signer_email}</strong> for{' '}
            <strong>{prospect?.company_name}</strong>.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              <strong>This will void the previous envelope.</strong> Use this when the prospect's
              envelope has expired or they've lost the original signing email.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Resending...
                </>
              ) : (
                'Yes, Resend Envelope'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Post-Provision Archive Prompt Modal
const ArchivePromptModal = ({ isOpen, onClose, prospect }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={() => onClose(false)} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-brand-950">User Provisioned Successfully</h3>
          </div>
          
          <p className="text-slate-600 mb-6">
            <strong>{prospect?.full_name}</strong> has been provisioned and will receive a welcome email at{' '}
            <strong>{prospect?.email}</strong>.
            <br /><br />
            Would you like to archive this prospect record?
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => onClose(false)}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              No, Keep Active
            </button>
            <button
              onClick={() => onClose(true)}
              className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
            >
              Yes, Archive
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Create Prospect Modal - Admin creates new user with Firebase Auth + Airtable record
const CreateProspectModal = ({ isOpen, onClose, onSubmit, isLoading, error }) => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    companyName: '',
    phone: ''
  })
  const [validationErrors, setValidationErrors] = useState({})

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: '', fullName: '', companyName: '', phone: '' })
      setValidationErrors({})
    }
  }, [isOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const errors = {}

    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email'
    }

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required'
    }

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validate()) {
      onSubmit(formData)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-brand-950">Create New Prospect</h3>
              <p className="text-sm text-slate-500">Set up account and send invite</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="create-email" className="block text-sm font-medium text-slate-700 mb-1">
                Email <span className="text-accent-600">*</span>
              </label>
              <input
                id="create-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  validationErrors.email ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="prospect@company.com"
              />
              {validationErrors.email && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="create-fullName" className="block text-sm font-medium text-slate-700 mb-1">
                Full Name <span className="text-accent-600">*</span>
              </label>
              <input
                id="create-fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  validationErrors.fullName ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Jane Smith"
              />
              {validationErrors.fullName && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.fullName}</p>
              )}
            </div>

            <div>
              <label htmlFor="create-companyName" className="block text-sm font-medium text-slate-700 mb-1">
                Company Name <span className="text-accent-600">*</span>
              </label>
              <input
                id="create-companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  validationErrors.companyName ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Acme Transport Ltd"
              />
              {validationErrors.companyName && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.companyName}</p>
              )}
            </div>

            <div>
              <label htmlFor="create-phone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone <span className="text-slate-400">(optional)</span>
              </label>
              <input
                id="create-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="+44 7700 900000"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm text-slate-700 mb-2">This will:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Create their login account
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Set up their prospect profile
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Send them a password setup link
                </li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create & Send Invite'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Success modal after creating a prospect
const CreateSuccessModal = ({ isOpen, onClose, email }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-brand-950">Prospect Created</h3>
          </div>

          <p className="text-slate-600 mb-4">
            A password setup link has been sent to <strong>{email}</strong>.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Once they set their password and log in, they'll be able to join the onboarding journey.
          </p>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// Demo stages available for preview (entry points only - other steps reachable via demo flow)
const DEMO_STAGES = [
  { stage: '03B_CONSENT_INITIATE', name: 'Consent Form' },
  { stage: '06_API_SETUP', name: 'API Setup' },
]

const AdminDashboard = () => {
  const {
    prospects,
    isLoadingProspects,
    error,
    fetchProspects,
    startImpersonation,
    stopImpersonation,
    impersonating,
    startDemoMode
  } = useAdmin()
  const { user, createUserAsAdmin, sendPasswordResetToUser } = useAuth()
  const navigate = useNavigate()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [stageFilter, setStageFilter] = useState('all')
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' })
  const [showArchived, setShowArchived] = useState(false)
  
  // Personalised demo initiation state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, prospect: null })
  const [isInitiating, setIsInitiating] = useState(false)
  const [initiateError, setInitiateError] = useState('')

  // Notes modal state
  const [notesModal, setNotesModal] = useState({ isOpen: false, prospect: null })
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [notesError, setNotesError] = useState('')

  // Archive modal state
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, prospect: null, isArchiving: true })
  const [isArchiving, setIsArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState('')

  // Provision modal state
  const [provisionModal, setProvisionModal] = useState({ isOpen: false, prospect: null })
  const [isProvisioning, setIsProvisioning] = useState(false)
  const [provisionError, setProvisionError] = useState('')

  // Resend envelope modal state
  const [resendModal, setResendModal] = useState({ isOpen: false, prospect: null })
  const [isResending, setIsResending] = useState(false)
  const [resendError, setResendError] = useState('')

  // Post-provision archive prompt state
  const [archivePrompt, setArchivePrompt] = useState({ isOpen: false, prospect: null })

  // Demo mode dropdown state
  const [showDemoMenu, setShowDemoMenu] = useState(false)

  // Create prospect modal state
  const [createModal, setCreateModal] = useState({ isOpen: false })
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createSuccess, setCreateSuccess] = useState({ isOpen: false, email: '' })

  // Organization users modal state
  const [orgUsersModal, setOrgUsersModal] = useState({ isOpen: false, prospect: null })

  useEffect(() => {
    if (impersonating) {
      stopImpersonation()
    }
  }, [])

  useEffect(() => {
    fetchProspects()
  }, [fetchProspects])

  const uniqueStages = useMemo(() => {
    const stages = [...new Set(prospects.map(p => p.onboarding_stage).filter(Boolean))]
    return stages.sort()
  }, [prospects])

  // Count archived prospects
  const archivedCount = useMemo(() => {
    return prospects.filter(p => p.archived === true).length
  }, [prospects])

  const filteredAndSortedProspects = useMemo(() => {
    let filtered = prospects

    // Filter by archived status
    if (!showArchived) {
      filtered = filtered.filter(p => p.archived !== true)
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(p => 
        (p.full_name || '').toLowerCase().includes(term) ||
        (p.email || '').toLowerCase().includes(term) ||
        (p.company_name || '').toLowerCase().includes(term)
      )
    }

    if (stageFilter !== 'all') {
      filtered = filtered.filter(p => p.onboarding_stage === stageFilter)
    }

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key]
      let bVal = b[sortConfig.key]

      if (aVal == null) aVal = ''
      if (bVal == null) bVal = ''

      if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0
      if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [prospects, searchTerm, stageFilter, sortConfig, showArchived])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const handleAssist = (prospect) => {
    const success = startImpersonation(prospect)
    if (success) {
      navigate('/onboarding')
    }
  }

  // Check if prospect can be moved to personalised demo
  const canInitiatePersonalised = (prospect) => {
    if (prospect.archived) return false
    const stage = prospect.onboarding_stage
    // Can initiate from intro stages (02, 03) only
    return stage === '02_INTEREST_CONFIRMED' || stage === '03_GENERIC_DEMO'
  }

  // Check if prospect can be provisioned
  // Must match n8n workflow #22 prerequisites
  const canProvision = (prospect) => {
    if (prospect.archived) return false
    if (prospect.provisioned) return false
    if (prospect.consent_status !== 'signed') return false
    if (!prospect.samsara_connected) return false
    if (!prospect.org_key) return false
    return true
  }

  // Check if prospect's DocuSign envelope can be resent
  // Only valid when they're waiting on signature
  const canResendEnvelope = (prospect) => {
    if (prospect.archived) return false
    if (prospect.onboarding_stage !== '04_CONSENT_PENDING') return false
    if (prospect.consent_status !== 'pending') return false
    return true
  }

  const handleInitiatePersonalised = async () => {
    if (!confirmModal.prospect) return
    
    setIsInitiating(true)
    setInitiateError('')
    
    try {
      const response = await n8nApi.initiatePersonalisedDemo(
        confirmModal.prospect.firebase_uid,
        user.uid
      )
      
      if (response.success) {
        setConfirmModal({ isOpen: false, prospect: null })
        await fetchProspects()
      } else {
        setInitiateError(response.error || 'Failed to initiate personalised demo')
      }
    } catch (err) {
      console.error('Failed to initiate personalised demo:', err)
      setInitiateError('An error occurred. Please try again.')
    } finally {
      setIsInitiating(false)
    }
  }

  // Handle saving notes
  const handleSaveNotes = async (prospectUid, notes) => {
    setIsSavingNotes(true)
    setNotesError('')
    
    try {
      const response = await n8nApi.updateProspectNotes(prospectUid, notes, user.uid)
      
      if (response.success) {
        setNotesModal({ isOpen: false, prospect: null })
        await fetchProspects()
      } else {
        setNotesError(response.error || 'Failed to save notes')
      }
    } catch (err) {
      console.error('Failed to save notes:', err)
      setNotesError('An error occurred. Please try again.')
    } finally {
      setIsSavingNotes(false)
    }
  }

  // Handle archive/unarchive
  const handleArchive = async () => {
    if (!archiveModal.prospect) return
    
    setIsArchiving(true)
    setArchiveError('')
    
    try {
      const response = await n8nApi.archiveProspect(
        archiveModal.prospect.firebase_uid,
        archiveModal.isArchiving,
        user.uid
      )
      
      if (response.success) {
        setArchiveModal({ isOpen: false, prospect: null, isArchiving: true })
        await fetchProspects()
      } else {
        setArchiveError(response.error || 'Failed to update archive status')
      }
    } catch (err) {
      console.error('Failed to archive/unarchive:', err)
      setArchiveError('An error occurred. Please try again.')
    } finally {
      setIsArchiving(false)
    }
  }

  // Handle provision user
  const handleProvision = async () => {
    if (!provisionModal.prospect) return
    
    setIsProvisioning(true)
    setProvisionError('')
    
    try {
      const response = await n8nApi.provisionUser(
        provisionModal.prospect.firebase_uid,
        user.uid
      )
      
      if (response.success) {
        const provisionedProspect = provisionModal.prospect
        setProvisionModal({ isOpen: false, prospect: null })
        
        // Show archive prompt after successful provision
        setArchivePrompt({ isOpen: true, prospect: provisionedProspect })
        
        // Refresh prospects list
        await fetchProspects()
      } else {
        setProvisionError(response.error || 'Failed to provision user')
      }
    } catch (err) {
      console.error('Failed to provision user:', err)
      setProvisionError('An error occurred. Please try again.')
    } finally {
      setIsProvisioning(false)
    }
  }

  // Handle resending DocuSign envelope
  const handleResendEnvelope = async () => {
    if (!resendModal.prospect) return

    setIsResending(true)
    setResendError('')

    try {
      const response = await n8nApi.resendConsentEnvelope(
        resendModal.prospect.firebase_uid,
        user.uid
      )

      if (response.success) {
        setResendModal({ isOpen: false, prospect: null })
        await fetchProspects()
      } else {
        setResendError(response.error || 'Failed to resend envelope')
      }
    } catch (err) {
      console.error('Failed to resend envelope:', err)
      setResendError('An error occurred. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  // Handle starting demo mode for a specific stage
  const handleStartDemo = (stage) => {
    setShowDemoMenu(false)
    startDemoMode(stage)
    const path = STAGE_INFO[stage]?.path
    if (path) {
      navigate(path)
    }
  }

  // Handle post-provision archive response
  const handleArchivePromptResponse = async (shouldArchive) => {
    if (shouldArchive && archivePrompt.prospect) {
      try {
        await n8nApi.archiveProspect(
          archivePrompt.prospect.firebase_uid,
          true,
          user.uid
        )
        await fetchProspects()
      } catch (err) {
        console.error('Failed to archive prospect:', err)
        // Don't show error - provisioning already succeeded
      }
    }
    setArchivePrompt({ isOpen: false, prospect: null })
  }

  // Handle create prospect
  const handleCreateProspect = async (formData) => {
    setIsCreating(true)
    setCreateError('')

    // Generate a random temporary password (will be reset via email)
    const tempPassword = crypto.randomUUID().slice(0, 16) + 'Aa1!'

    try {
      // Step 1: Create Firebase user using secondary auth instance
      console.log('📤 Creating Firebase user...')
      const firebaseUser = await createUserAsAdmin(formData.email, tempPassword)
      console.log('✅ Firebase user created:', firebaseUser.uid)

      // Step 2: Create Airtable record via existing n8n webhook
      try {
        console.log('📤 Creating Airtable record...')
        const response = await n8nApi.createUserWithDetails(firebaseUser.uid, {
          email: formData.email,
          full_name: formData.fullName,
          company_name: formData.companyName,
          phone: formData.phone || ''
        })
        console.log('✅ Airtable record created:', response)
      } catch (airtableError) {
        console.error('⚠️ Airtable creation failed:', airtableError)
        // Continue anyway - user can complete profile on first login
        setCreateError('Account created but profile setup had an issue. The user can complete their profile when they log in.')
      }

      // Step 3: Send password setup email
      try {
        console.log('📤 Sending password setup email...')
        await sendPasswordResetToUser(formData.email)
        console.log('✅ Password setup email sent')
      } catch (emailError) {
        console.error('⚠️ Password setup email failed:', emailError)
        setCreateError('Account created but could not send the password setup email. You may need to trigger this manually from Firebase Console.')
        await fetchProspects()
        return
      }

      // Success - close modal, show success message, and refresh list
      setCreateModal({ isOpen: false })
      setCreateSuccess({ isOpen: true, email: formData.email })
      await fetchProspects()

    } catch (err) {
      console.error('❌ Create prospect failed:', err)
      // Translate Firebase error codes to user-friendly messages
      if (err.code === 'auth/email-already-in-use') {
        setCreateError('A user with this email already exists.')
      } else if (err.code === 'auth/invalid-email') {
        setCreateError('Please enter a valid email address.')
      } else {
        setCreateError('Failed to create prospect. Please try again.')
      }
    } finally {
      setIsCreating(false)
    }
  }

  const stats = {
    total: prospects.filter(p => p.archived !== true).length,
    archived: archivedCount,
    introBooked: prospects.filter(p => p.onboarding_stage === '03_GENERIC_DEMO' && p.archived !== true).length,
    inPersonalised: prospects.filter(p => 
      ['03B_CONSENT_INITIATE', '04_CONSENT_PENDING', '05_CONSENT_SIGNED', '06_API_SETUP', '07_API_CONNECTED'].includes(p.onboarding_stage) && p.archived !== true
    ).length,
    demoScheduled: prospects.filter(p => p.onboarding_stage === '10_DEMO_SCHEDULED' && p.archived !== true).length,
    provisioned: prospects.filter(p => p.provisioned === true && p.archived !== true).length
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoadingProspects) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        <span className="ml-3 text-slate-600">Loading prospects...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={fetchProspects} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Active Prospects</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Intro Booked</p>
          <p className="text-2xl font-bold text-purple-600">{stats.introBooked}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">In Personalised Flow</p>
          <p className="text-2xl font-bold text-amber-600">{stats.inPersonalised}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Demo Scheduled</p>
          <p className="text-2xl font-bold text-green-600">{stats.demoScheduled}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Provisioned</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.provisioned}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Archived</p>
          <p className="text-2xl font-bold text-slate-400">{stats.archived}</p>
        </div>
      </div>

      {/* Error displays */}
      {initiateError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{initiateError}</p>
        </div>
      )}
      {notesError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{notesError}</p>
        </div>
      )}
      {archiveError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{archiveError}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="sm:w-64 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="all">All Stages</option>
            {uniqueStages.map(stage => (
              <option key={stage} value={stage}>{STAGE_INFO[stage]?.name || stage}</option>
            ))}
          </select>

          {/* Show archived toggle */}
          <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-slate-700">Show archived</span>
            {archivedCount > 0 && (
              <span className="text-slate-400">({archivedCount})</span>
            )}
          </label>

          <button
            onClick={fetchProspects}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>

          {/* Demo Step button with dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDemoMenu(!showDemoMenu)}
              className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Demo Step
              <svg className={`w-4 h-4 transition-transform ${showDemoMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDemoMenu && (
              <>
                {/* Backdrop to close menu */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDemoMenu(false)}
                />
                {/* Dropdown menu */}
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-20">
                  <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase border-b border-slate-100">
                    Preview Onboarding Step
                  </div>
                  {DEMO_STAGES.map(({ stage, name }) => (
                    <button
                      key={stage}
                      onClick={() => handleStartDemo(stage)}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-purple-400" />
                      {name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Create Prospect button */}
          <button
            onClick={() => setCreateModal({ isOpen: true })}
            className="px-4 py-2 text-sm text-white bg-brand-600 hover:bg-brand-700 rounded-lg flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Create Prospect
          </button>
        </div>

        <div className="mt-3 text-sm text-slate-500">
          Showing {filteredAndSortedProspects.length} of {prospects.length} prospects
          {showArchived && archivedCount > 0 && ` (including ${archivedCount} archived)`}
        </div>
      </div>

      {/* Prospects table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('full_name')}>
                  <div className="flex items-center gap-1">Name <SortIcon column="full_name" sortConfig={sortConfig} /></div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('company_name')}>
                  <div className="flex items-center gap-1">Company <SortIcon column="company_name" sortConfig={sortConfig} /></div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('onboarding_stage')}>
                  <div className="flex items-center gap-1">Stage <SortIcon column="onboarding_stage" sortConfig={sortConfig} /></div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Meeting
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Notes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredAndSortedProspects.map((prospect) => (
                <tr 
                  key={prospect.id} 
                  className={`hover:bg-slate-50 ${prospect.archived ? 'opacity-50 bg-slate-50' : ''}`}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{prospect.full_name || '—'}</div>
                        <div className="text-xs text-slate-500">{prospect.email || ''}</div>
                      </div>
                      {prospect.archived && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                          Archived
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                    {prospect.company_name || '—'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <StageBadge stage={prospect.onboarding_stage} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {prospect.intro_meeting_scheduled ? (
                      <div>
                        <span className="text-xs font-medium text-purple-600">Intro</span>
                        <div className="text-xs text-slate-500">{formatDateTime(prospect.intro_meeting_scheduled_at)}</div>
                      </div>
                    ) : prospect.meeting_scheduled ? (
                      <div>
                        <span className="text-xs font-medium text-green-600">Demo</span>
                        <div className="text-xs text-slate-500">{formatDateTime(prospect.meeting_scheduled_at)}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => setNotesModal({ isOpen: true, prospect })}
                      className="group flex items-center gap-1.5 text-left max-w-[150px]"
                      title={prospect.notes || 'Add notes'}
                    >
                      {prospect.notes ? (
                        <>
                          <span className="text-xs text-slate-600 truncate">{prospect.notes}</span>
                          <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 group-hover:text-brand-600 italic flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add note
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      {!prospect.archived && (
                        <>
                          <button
                            onClick={() => handleAssist(prospect)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg"
                            title="Assist prospect"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Assist
                          </button>
                          
                          {canInitiatePersonalised(prospect) && (
                            <button
                              onClick={() => setConfirmModal({ isOpen: true, prospect })}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-accent-700 bg-accent-50 hover:bg-accent-100 rounded-lg"
                              title="Start personalised demo pathway"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Personalised
                            </button>
                          )}

                          {canResendEnvelope(prospect) && (
                            <button
                              onClick={() => setResendModal({ isOpen: true, prospect })}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg"
                              title="Resend DocuSign envelope"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Resend envelope
                            </button>
                          )}

                          {canProvision(prospect) && (
                            <button
                              onClick={() => setProvisionModal({ isOpen: true, prospect })}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg"
                              title="Provision user in FleetVision"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                              </svg>
                              Provision
                            </button>
                          )}

                          {/* TODO: Re-enable multi-user provisioning after backend engineer testing.
                              Requires testing: Firebase account creation, n8n provision-organization-users webhook,
                              password reset emails. See OrganizationUsersModal.jsx. Visibility condition: prospect.org_key && !prospect.archived */}
                          {false && prospect.org_key && !prospect.archived && (
                            <button
                              onClick={() => setOrgUsersModal({ isOpen: true, prospect })}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg"
                              title="Manage organization users"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              Users
                            </button>
                          )}

                          {prospect.provisioned && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Provisioned
                            </span>
                          )}
                          
                          <button
                            onClick={() => setArchiveModal({ isOpen: true, prospect, isArchiving: true })}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg"
                            title="Archive prospect"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            Archive
                          </button>
                        </>
                      )}
                      
                      {prospect.archived && (
                        <button
                          onClick={() => setArchiveModal({ isOpen: true, prospect, isArchiving: false })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg"
                          title="Unarchive prospect"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Unarchive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedProspects.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {prospects.length === 0 ? 'No prospects found.' : 'No prospects match your filters.'}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, prospect: null })}
        onConfirm={handleInitiatePersonalised}
        prospect={confirmModal.prospect}
        isLoading={isInitiating}
      />

      {/* Notes Modal */}
      <NotesModal
        isOpen={notesModal.isOpen}
        onClose={() => setNotesModal({ isOpen: false, prospect: null })}
        onSave={handleSaveNotes}
        prospect={notesModal.prospect}
        isLoading={isSavingNotes}
      />

      {/* Archive Modal */}
      <ArchiveModal
        isOpen={archiveModal.isOpen}
        onClose={() => setArchiveModal({ isOpen: false, prospect: null, isArchiving: true })}
        onConfirm={handleArchive}
        prospect={archiveModal.prospect}
        isArchiving={archiveModal.isArchiving}
        isLoading={isArchiving}
      />

      {/* Provision Modal */}
      <ProvisionModal
        isOpen={provisionModal.isOpen}
        onClose={() => setProvisionModal({ isOpen: false, prospect: null })}
        onConfirm={handleProvision}
        prospect={provisionModal.prospect}
        isLoading={isProvisioning}
        error={provisionError}
      />

      {/* Resend Envelope Modal */}
      <ResendEnvelopeModal
        isOpen={resendModal.isOpen}
        onClose={() => {
          setResendModal({ isOpen: false, prospect: null })
          setResendError('')
        }}
        onConfirm={handleResendEnvelope}
        prospect={resendModal.prospect}
        isLoading={isResending}
        error={resendError}
      />

      {/* Post-Provision Archive Prompt */}
      <ArchivePromptModal
        isOpen={archivePrompt.isOpen}
        onClose={handleArchivePromptResponse}
        prospect={archivePrompt.prospect}
      />

      {/* Create Prospect Modal */}
      <CreateProspectModal
        isOpen={createModal.isOpen}
        onClose={() => {
          setCreateModal({ isOpen: false })
          setCreateError('')
        }}
        onSubmit={handleCreateProspect}
        isLoading={isCreating}
        error={createError}
      />

      {/* Create Prospect Success Modal */}
      <CreateSuccessModal
        isOpen={createSuccess.isOpen}
        onClose={() => setCreateSuccess({ isOpen: false, email: '' })}
        email={createSuccess.email}
      />

      {/* Organization Users Modal */}
      <OrganizationUsersModal
        isOpen={orgUsersModal.isOpen}
        onClose={() => setOrgUsersModal({ isOpen: false, prospect: null })}
        prospect={orgUsersModal.prospect}
        onUsersChanged={fetchProspects}
      />
    </div>
  )
}

export default AdminDashboard
